import { useEffect, useState } from "react";
import { Footprints, Heart, Users, ExternalLink, Sparkles, BookOpen, Mic, Briefcase, Cake } from "lucide-react";
import { useCmsContent, renderMarkers } from "@/lib/cms";
import api from "@/lib/api";
import BirthdayBanner from "@/components/BirthdayBanner";

const VALUE_ICONS = [Footprints, Heart, Users];
const VALUE_STYLES = [
  { bg: "bg-green-100", fg: "text-green-700" },
  { bg: "bg-amber-100", fg: "text-amber-700" },
  { bg: "bg-slate-800", fg: "text-amber-300" },
];

// ---------- Profile data (canonical bios from the parent) ----------
const PROFILES = [
  {
    key: "josiah",
    display_name: "Big Sib Josiah",
    full_name: "Josiah Williams Rutledge",
    role: "Big Sib · Lead Dasher · Founder",
    accent: "from-green-200 via-emerald-200 to-green-300",
    border: "border-green-300",
    avatarEmoji: "🏃‍♂️",
    photo: "/team/josiah.jpg",
    bio: `Josiah Williams Rutledge is the voice of the future — not just because of his age, but because of his perspective, creativity, and commitment to helping young people step into who they are becoming.

Known as **Big Sib Josiah**, he is a motivational and public speaker, a two-time published author, an AMC Theater producer and director, and the founder of **Jobibbidy's Enterprise**. Through storytelling, leadership, and real-life experiences, Josiah encourages students to build confidence, find their voice, and think bigger about their future.

As one of the leaders behind Bag Dash Kids, Josiah brings vision, responsibility, creativity, and purpose to the work. He believes young people can lead, serve, build, and create meaningful impact right where they are.`,
    interestPills: [
      { icon: Mic, label: "Public speaking" },
      { icon: BookOpen, label: "2x Published author" },
      { icon: Sparkles, label: "AMC Theater producer" },
    ],
    mission: "Helping young people step into who they are becoming.",
    links: [
      { label: "Book Big Sib Josiah", href: "https://www.josiahwr.com", primary: true },
      { label: "Learn More About Josiah", href: "https://www.josiahwr.com" },
    ],
    supportLabel: "Support Big Sib Josiah",
  },
  {
    key: "jayla",
    display_name: "Lil Sib Jayla",
    full_name: "Jayla",
    role: "Lil Sib · Bag Whisperer · Founder of Jayla's Heart",
    accent: "from-pink-200 via-rose-200 to-pink-300",
    border: "border-pink-300",
    avatarEmoji: "🏃‍♀️",
    photo: "/team/jayla.jpg",
    bio: `Jayla is a creative, caring, and thoughtful young leader with a big heart for people and her community. Known as **Lil Sib Jayla**, she enjoys spending time with her family and friends, bonding with her siblings, reading books, singing, and creating arts and crafts.

Jayla is also working toward establishing **Jayla's Heart**, a nonprofit created to provide a safe, welcoming, and supportive environment for individuals experiencing homelessness. Through shelter, food, essential resources, and connections to long-term support services, Jayla hopes to help reduce homelessness and make a lasting difference in her community.

When she grows up, Jayla dreams of becoming a singer and continuing to use her voice, creativity, and compassion to inspire and help others.`,
    interestPills: [
      { icon: Heart, label: "Jayla's Heart (nonprofit)" },
      { icon: Sparkles, label: "Singing · Arts & crafts" },
      { icon: BookOpen, label: "Reading & creativity" },
    ],
    mission: "Helping reduce homelessness and lifting up her community.",
    links: [],
    supportLabel: "Support Lil Sib Jayla",
  },
  {
    key: "donja",
    display_name: "Mom Manager Donja",
    full_name: "Donja Williams",
    role: "Mom Manager · Founder, Freedom In Me LLC",
    accent: "from-amber-100 via-yellow-100 to-orange-200",
    border: "border-amber-300",
    avatarEmoji: "👩🏽‍💼",
    photo: "/team/donja.jpg",
    bio: `Donja Williams is the **Mom Manager** behind Bag Dash Kids — the builder, strategist, encourager, and steady hand helping Big Sib Josiah and Lil Sib Jayla turn their creativity, responsibility, and service into a real youth-led business.

Her story is rooted in moving from **poverty to purpose**. As a single mother, Donja is committed to fighting the statistics, breaking cycles, and raising young entrepreneurs who understand leadership, ownership, service, and faith in action. Bag Dash Kids is more than a business to her family — it is a step toward legacy, discipline, and possibility.

Donja is also the founder of **Freedom In Me LLC**, where she helps organizations and small businesses build stronger systems, training, and digital infrastructure. Through the **AI Build Lab**, she teaches others how to use AI to build websites, apps, workflows, and tools that help bring their ideas to life. She also leads **Freedom In Me Business Showers**, a platform created to support early-stage entrepreneurs with training, visibility, funding, resources, and community support.

While customers may see the kids out front, Mommy is never far. Donja helps build the systems, manage the vision, support the operations, and teach her children what it means to serve with excellence.

One of Donja's personal promises is to help her family reach a major milestone: **securing a home for her children by the end of July 2027**. Every pickup, every lesson, every late night, and every bold step is part of pushing that promise forward.`,
    interestPills: [
      { icon: Briefcase, label: "Founder, Freedom In Me LLC" },
      { icon: Sparkles, label: "AI Build Lab" },
      { icon: Users, label: "Business Showers" },
    ],
    mission: "From poverty to purpose. Securing a home for her kids by July 2027.",
    links: [
      { label: "Meet Mom Manager Donja", href: "https://www.donjawilliams.com", primary: true },
      { label: "Learn About Freedom In Me", href: "https://www.itsthefreedominme.com" },
      { label: "Explore the AI Build Lab", href: "https://www.donjawilliams.com" },
      { label: "Get training & funding for your small business", href: "https://www.businessshowers.org" },
    ],
    supportLabel: null, // Donja card has external CTAs, not a birthday-tip button
  },
];

// Simple markdown-ish renderer: **bold** + paragraphs
function renderBio(text) {
  return text.split(/\n\n+/).map((para, i) => {
    const parts = para.split(/(\*\*[^*]+\*\*)/g).map((seg, j) =>
      seg.startsWith("**") && seg.endsWith("**")
        ? <strong key={j} className="text-slate-900 font-bold">{seg.slice(2, -2)}</strong>
        : <span key={j}>{seg}</span>
    );
    return <p key={i} className="text-slate-700 leading-relaxed mb-3 last:mb-0">{parts}</p>;
  });
}

export default function AboutPage() {
  const { c } = useCmsContent("about");
  const values = c["about.values"] || [];
  const [birthdaySupport, setBirthdaySupport] = useState({ key: null });

  // Listen for "support this sibling" clicks via window event so the
  // BirthdayBanner modal can be reused without duplicating Stripe checkout.
  useEffect(() => {
    const handler = (e) => setBirthdaySupport({ key: e.detail });
    window.addEventListener("bagdash-support-sibling", handler);
    return () => window.removeEventListener("bagdash-support-sibling", handler);
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
      <h1 className="font-fredoka text-4xl sm:text-5xl font-bold text-slate-900" data-testid="about-title">{c["about.title"] || "Who we are"}</h1>
      <p
        className="text-lg text-slate-700 mt-3 leading-relaxed"
        dangerouslySetInnerHTML={{ __html: renderMarkers(c["about.intro"] || "") }}
      />

      {/* Birthday banner — appears only during a sibling's birthday week */}
      <div className="mt-6"><BirthdayBanner /></div>

      <div className="grid sm:grid-cols-3 gap-3 mt-8">
        {values.map((v, i) => {
          const Icon = VALUE_ICONS[i] || Footprints;
          const sty = VALUE_STYLES[i] || VALUE_STYLES[0];
          return (
            <div key={i} className="card-clay p-5 text-center">
              <div className={`w-12 h-12 rounded-2xl ${sty.bg} ${sty.fg} flex items-center justify-center mx-auto`}><Icon strokeWidth={2.5}/></div>
              <div className="font-fredoka font-bold mt-3">{v.title}</div>
              <p className="text-sm text-slate-600 mt-1">{v.body}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-10">
        <h2 className="font-fredoka text-3xl sm:text-4xl font-bold text-slate-900 mb-2 flex items-center gap-2" data-testid="meet-team-title">
          <Users strokeWidth={2.5}/> Meet the Bag Dash Kids
        </h2>
        <p className="text-slate-600 mb-6">The faces, dreams, and stories behind every pickup.</p>

        <div className="space-y-5">
          {PROFILES.map((p) => (
            <ProfileCard key={p.key} profile={p} />
          ))}
        </div>
      </div>

      {/* Support section */}
      <SupportSection />
    </div>
  );
}

function ProfileCard({ profile }) {
  return (
    <article className={`card-clay overflow-hidden border-4 ${profile.border}`} data-testid={`profile-${profile.key}`}>
      <div className={`bg-gradient-to-br ${profile.accent} px-5 py-6 flex items-start gap-4 relative`}>
        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-3xl bg-white/70 backdrop-blur border-2 border-white shadow-md overflow-hidden flex items-center justify-center text-5xl sm:text-6xl shrink-0">
          {profile.photo ? (
            <img
              src={profile.photo}
              alt={profile.display_name}
              loading="lazy"
              className="w-full h-full object-cover"
              data-testid={`profile-${profile.key}-photo`}
            />
          ) : (
            profile.avatarEmoji
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-700/70">{profile.role}</div>
          <h3 className="font-fredoka text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">{profile.display_name}</h3>
          <div className="text-sm text-slate-700 mt-0.5">{profile.full_name}</div>
        </div>
      </div>
      <div className="p-5">
        <div>{renderBio(profile.bio)}</div>

        <div className="mt-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">Interests &amp; work</div>
          <div className="flex flex-wrap gap-2">
            {profile.interestPills.map((pill, i) => {
              const Icon = pill.icon;
              return (
                <span key={i} className="inline-flex items-center gap-1.5 bg-slate-100 border border-slate-200 text-slate-700 rounded-full px-3 py-1 text-xs font-semibold" data-testid={`profile-${profile.key}-pill-${i}`}>
                  <Icon size={12} strokeWidth={2.5}/> {pill.label}
                </span>
              );
            })}
          </div>
        </div>

        <div className="mt-4 rounded-2xl bg-slate-50 border-2 border-slate-100 px-4 py-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Mission</div>
          <div className="text-sm text-slate-800 font-semibold mt-0.5">{profile.mission}</div>
        </div>

        {(profile.links.length > 0 || profile.supportLabel) && (
          <div className="mt-4 flex flex-wrap gap-2" data-testid={`profile-${profile.key}-ctas`}>
            {profile.links.map((l, i) => (
              <a
                key={i}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                className={`inline-flex items-center gap-1.5 ${l.primary ? "btn-push btn-push-navy" : "btn-push btn-push-outline"} px-4 py-2 text-sm`}
                data-testid={`profile-${profile.key}-link-${i}`}
              >
                {l.label} <ExternalLink size={14}/>
              </a>
            ))}
            {profile.supportLabel && (
              <button
                onClick={() => window.dispatchEvent(new CustomEvent("bagdash-support-sibling", { detail: profile.key }))}
                className="btn-push btn-push-pink px-4 py-2 text-sm inline-flex items-center gap-1.5"
                data-testid={`profile-${profile.key}-support`}
              >
                <Cake size={14} strokeWidth={2.5}/> {profile.supportLabel}
              </button>
            )}
          </div>
        )}
      </div>
    </article>
  );
}

function SupportSection() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/birthday-status").then((r) => setData(r.data)).catch(() => {}); }, []);

  return (
    <div className="mt-12 card-clay p-6 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white border-slate-700" data-testid="support-section">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 rounded-2xl bg-amber-400 text-slate-900 flex items-center justify-center shrink-0"><Heart strokeWidth={2.5}/></div>
        <div className="flex-1">
          <h3 className="font-fredoka text-2xl font-bold">Support the Bag Dash Kids</h3>
          <p className="text-slate-300 mt-1 text-sm leading-relaxed">
            Bag Dash Kids is more than a pickup service. It is a youth-led business helping Big Sib Josiah and Lil Sib Jayla learn entrepreneurship, leadership, service, and responsibility while serving their community.
          </p>
          <ul className="mt-3 text-sm text-slate-300 space-y-1 list-disc list-inside marker:text-amber-300">
            <li>Add a tip after checkout</li>
            <li>Send birthday support during birthday week</li>
            <li>Support Big Sib Josiah · Support Lil Sib Jayla</li>
          </ul>
          <div className="mt-4 flex flex-wrap gap-2">
            {(data?.siblings || []).map((s) => (
              <button
                key={s.key}
                onClick={() => window.dispatchEvent(new CustomEvent("bagdash-support-sibling", { detail: s.key }))}
                className="btn-push btn-push-amber px-4 py-2 text-sm"
                data-testid={`support-cta-${s.key}`}
              >
                🎂 Support {s.display_name}
              </button>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-3">Support is fully optional and never required to book a pickup.</p>
        </div>
      </div>
    </div>
  );
}
